/**
 * useAiActions — Iter 156.3 (Feb 27, 2026, Yash).
 *
 * Extracted from ChatsPage.jsx. Owns the 4 AI-button handlers that fire
 * Claude Sonnet 4.5 endpoints: suggest-reply, extract-details, detect-
 * sentiment, smart-categorize.
 *
 * The `handleAiExtract` body is the heaviest (~80 lines, including the
 * payment-mode normalisation + discount-percent → discount-amount maths)
 * which is exactly why it was worth moving out of the main page.
 */
import { aiApi } from "../lib/api";
import { toast } from "sonner";

// Iter 153 (Feb 2026, Yash) — Item #6 — auto-fetch must populate payment
// mode, custom COD charge, custom discount + amount + item codes, not
// just contact info. Backend prompt was extended to return these from
// the chat history.
const PAYMENT_MODES_LC = {
  "upi": "UPI", "qr": "UPI", "qr code": "UPI",
  "card": "Card", "credit card": "Card", "debit card": "Card",
  "net banking": "Net Banking", "netbanking": "Net Banking",
  "cod": "COD", "cash on delivery": "COD", "cash": "COD",
  "bank transfer": "Bank Transfer", "neft": "Bank Transfer",
  "rtgs": "Bank Transfer", "imps": "Bank Transfer",
  "special pay": "Special Pay", "proforma": "Proforma",
};

const normalisePaymentMode = (raw) => {
  const v = (raw || "").toString().trim().toLowerCase();
  if (!v) return null;
  return PAYMENT_MODES_LC[v] || (
    Object.values(PAYMENT_MODES_LC).find(m => m.toLowerCase() === v) || null
  );
};

export const useAiActions = ({
  selectedChat,
  setAiLoading,
  setReplyText,
  inputRef,
  setCustForm,
  setDetailsTab,
  setSelectedLead,
  loadMessages,
  fetchChats,
}) => {
  const handleAiSuggest = async () => {
    if (!selectedChat) return;
    setAiLoading("suggest");
    try {
      const res = await aiApi.suggestReply(selectedChat.id);
      setReplyText(res.data.suggestion);
      toast.success("AI reply suggested — edit before sending");
      inputRef.current?.focus();
    } catch (e) {
      // Surface the real reason so we can debug live (Yash, Apr 27).
      const detail = e?.response?.data?.detail || e?.message || "Unknown error";
      const status = e?.response?.status;
      console.error("[ai-draft] failed:", status, detail, e);
      toast.error(`AI draft failed: ${status ? `HTTP ${status} — ` : ""}${detail.slice(0, 140)}`);
    } finally {
      setAiLoading("");
    }
  };

  const handleAiExtract = async () => {
    if (!selectedChat) return;
    setAiLoading("extract");
    try {
      const res = await aiApi.extractDetails(selectedChat.id);
      const d = res.data.details || {};
      const normalizedPayment = normalisePaymentMode(d.payment_mode);
      setCustForm(prev => ({
        ...prev,
        customer_name: d.customer_name || prev.customer_name,
        customer_email: d.customer_email || prev.customer_email,
        customer_phone: d.customer_phone || prev.customer_phone,
        customer_address: d.customer_address || prev.customer_address,
        customer_pincode: d.customer_pincode || prev.customer_pincode,
        // Numeric fields — only overwrite if extracted (don't blow away
        // values the agent already typed manually).
        amount: (d.amount != null && d.amount !== "" && d.amount !== 0)
          ? String(d.amount) : prev.amount,
        // Iter 153.3 (Feb 7, 2026, Yash) — UI dropped the Discount %
        // field. If the LLM extracted a percent AND we have an amount,
        // auto-compute the rupee value and pre-fill `discount_amount`.
        // Otherwise fall back to the raw `discount_amount` if the LLM
        // surfaced it.
        discount_percent: "",  // always cleared — UI no longer accepts it
        discount_amount: (() => {
          const pct = d.discount_percent != null && d.discount_percent !== ""
            ? Number(d.discount_percent) : 0;
          const amt = d.amount != null && d.amount !== ""
            ? Number(d.amount) : Number(prev.amount || 0);
          if (pct > 0 && amt > 0) {
            return String(Math.round(amt * pct / 100));
          }
          if (d.discount_amount != null && d.discount_amount !== "" && d.discount_amount !== 0) {
            return String(d.discount_amount);
          }
          return prev.discount_amount;
        })(),
        payment_mode: normalizedPayment || prev.payment_mode,
        item_codes: d.item_codes
          ? (Array.isArray(d.item_codes) ? d.item_codes.join(", ") : String(d.item_codes))
          : prev.item_codes,
      }));
      setDetailsTab("order");
      const filledFields = [
        d.customer_name && "name",
        d.customer_pincode && "pincode",
        normalizedPayment && `payment (${normalizedPayment})`,
        (d.discount_percent || d.discount_amount) && "discount",
        d.amount && "amount",
        d.item_codes && "items",
      ].filter(Boolean);
      toast.success(
        filledFields.length
          ? `Extracted: ${filledFields.join(", ")} — review & edit`
          : "Details extracted from chat — review and edit"
      );
    } catch (e) { toast.error("Failed to extract details"); }
    finally { setAiLoading(""); }
  };

  const handleAiSentiment = async () => {
    if (!selectedChat) return;
    setAiLoading("sentiment");
    try {
      const res = await aiApi.detectSentiment(selectedChat.id);
      const s = res.data;
      toast.info(`Sentiment: ${s.sentiment} (${Math.round((s.confidence || 0) * 100)}%) — ${s.reason || ""}`);
      setSelectedLead(prev => prev ? { ...prev, sentiment: s.sentiment } : prev);
    } catch (e) { toast.error("Sentiment detection failed"); }
    finally { setAiLoading(""); }
  };

  const handleAiCategorize = async () => {
    if (!selectedChat) return;
    setAiLoading("categorize");
    try {
      const res = await aiApi.smartCategorize(selectedChat.id);
      if (res.data.category) {
        toast.success(`Category: ${res.data.category}`);
        await loadMessages(selectedChat.id);
        fetchChats(1, false, true);
      } else {
        toast.info("Could not determine category");
      }
    } catch (e) { toast.error("Categorization failed"); }
    finally { setAiLoading(""); }
  };

  return { handleAiSuggest, handleAiExtract, handleAiSentiment, handleAiCategorize };
};
